/* goodnight-mattress-quiz.js
   Custom element that drives the 4-step mattress quiz on /pages/the-pebble-quiz.
   Scoring algorithm ports + extends the live `main-mattress-quiz.liquid` section
   to cover Pure (live snippet did not include Pure scoring).
*/

(function () {
  const QUESTION_KEYS = ['position', 'firmness', 'hot', 'size'];

  function score(answers) {
    const s = {
      'the-pebble-lite': 0,
      'the-pebble-luxe': 0,
      'the-pebble-ultra-luxe': 0,
      'the-pebble-pure-mattress': 0
    };

    switch (answers.firmness) {
      case 'soft':
        s['the-pebble-luxe'] += 3;
        s['the-pebble-pure-mattress'] += 2;
        s['the-pebble-lite'] += 1;
        s['the-pebble-ultra-luxe'] += 1;
        break;
      case 'medium':
        s['the-pebble-luxe'] += 2;
        s['the-pebble-lite'] += 2;
        s['the-pebble-ultra-luxe'] += 2;
        s['the-pebble-pure-mattress'] += 1;
        break;
      case 'firm':
        s['the-pebble-ultra-luxe'] += 3;
        s['the-pebble-lite'] += 2;
        s['the-pebble-pure-mattress'] += 1;
        break;
    }

    switch (answers.position) {
      case 'side':
        s['the-pebble-luxe'] += 1;
        s['the-pebble-pure-mattress'] += 1;
        break;
      case 'back':
        s['the-pebble-ultra-luxe'] += 1;
        s['the-pebble-pure-mattress'] += 1;
        break;
      case 'combo':
        s['the-pebble-ultra-luxe'] += 1;
        s['the-pebble-lite'] += 1;
        s['the-pebble-luxe'] += 1;
        break;
    }

    switch (answers.hot) {
      case 'no':
        s['the-pebble-lite'] += 1;
        s['the-pebble-luxe'] += 1;
        break;
      case 'sometimes':
        s['the-pebble-ultra-luxe'] += 1;
        s['the-pebble-pure-mattress'] += 1;
        break;
      case 'yes':
        s['the-pebble-ultra-luxe'] += 2;
        s['the-pebble-pure-mattress'] += 2;
        break;
    }

    // Tiebreaker rank when raw scores tie:
    // Latex-Luxe > Luxe > Pure > Lite
    const tiebreak = {
      'the-pebble-ultra-luxe': 4,
      'the-pebble-luxe': 3,
      'the-pebble-pure-mattress': 2,
      'the-pebble-lite': 1
    };

    return Object.keys(s)
      .map(handle => ({ handle, score: s[handle], tb: tiebreak[handle] }))
      .sort((a, b) => (b.score - a.score) || (b.tb - a.tb));
  }

  const ANSWER_LABEL = {
    position: { side: 'Side sleeper', back: 'Back sleeper', combo: 'Combo sleeper' },
    firmness: { soft: 'Soft', medium: 'Medium', firm: 'Firm' },
    hot:      { no: 'Sleeps cool', sometimes: 'Sometimes warm', yes: 'Hot sleeper' },
    size:     { 'twin-xl': 'Twin XL', 'full': 'Full', 'queen': 'Queen', 'king': 'King', 'cal-king': 'California King' }
  };

  const WHY = {
    position: {
      side:  'You sleep like a shrimp, so we picked something that hugs your shoulders and hips without flattening them.',
      back:  'Vampire-style sleeper? Your spine wants a surface that keeps it aligned while you lie there looking majestic.',
      combo: 'Rotisserie sleeper detected. You need something responsive so every position feels like the right one.'
    },
    firmness: {
      soft:   'You wanted cloud mode. This one lets you sink in without feeling like you are being eaten by your bed.',
      medium: 'Goldilocks zone locked in. Not too soft, not too firm. Just "ahh."',
      firm:   'You like support. This mattress does not let you sink. It lets you float with purpose.'
    },
    hot: {
      no:        'You sleep cool, so we leaned into the cradle. No fancy cooling tech wasted on you.',
      sometimes: 'Mild temperature variation handled — this build breathes when you need it to.',
      yes:       'Built for hot sleepers — latex breathes, foam adapts. You will not be a rotisserie tonight.'
    },
    size: {
      'twin-xl':  'Twin XL — efficient choice. Made-to-order in your size.',
      'full':     'Full — the in-between size. Made just for you.',
      'queen':    'Queen — the people’s choice. Most popular for a reason.',
      'king':     'King — room to spread out. We get it.',
      'cal-king': 'California King — long and lean. The runway model of mattresses.'
    }
  };

  function cardHTML(productData, label) {
    const labelHTML = label ? `<span class="gn-quiz__best-label">${label}</span>` : '';
    const img = productData.image
      ? `<img class="gn-card__media" src="${productData.image}" alt="${productData.title}" loading="lazy">`
      : '';
    return `
      <a class="gn-card gn-card--quiz" href="${productData.url}">
        <div class="gn-card__media">${labelHTML}${img}</div>
        <div class="gn-card__body">
          <h3 class="gn-card__title">${productData.title}</h3>
          <div class="gn-card__price"><span>From</span> <span class="gn-card__price-amount">${productData.price_from}</span></div>
          <span class="gn-card__cta ps-btn-primary">View →</span>
        </div>
      </a>`;
  }

  class GoodnightMattressQuiz extends HTMLElement {
    connectedCallback() {
      this.step = 0;
      this.answers = { position: null, firmness: null, hot: null, size: null };

      try {
        this.products = JSON.parse(this.querySelector('[data-gn-products]').textContent);
      } catch (e) {
        console.error('Quiz: failed to parse product data', e);
        this.products = {};
      }

      this.form = this.querySelector('[data-gn-form]');
      this.steps = this.querySelectorAll('[data-gn-step]');
      this.backBtn = this.querySelector('[data-gn-back]');
      this.nextBtn = this.querySelector('[data-gn-next]');
      this.submitBtn = this.querySelector('[data-gn-submit]');
      this.skipBtn = this.querySelector('[data-gn-skip]');
      this.emailInput = this.querySelector('[data-gn-email]');
      this.emailError = this.querySelector('[data-gn-email-error]');
      this.quizNoteField = this.querySelector('[data-gn-quiz-note]');
      this.stepLabel = this.querySelector('[data-gn-step-label]');
      this.progress = this.querySelectorAll('.gn-quiz__dot');
      this.progressBar = this.querySelector('[data-gn-progress]');
      this.announcer = this.querySelector('[data-gn-step-announcer]');
      this.result = this.querySelector('[data-gn-result]');
      this.restart = this.querySelector('[data-gn-restart]');

      this.form.addEventListener('change', (e) => this.onChange(e));
      this.form.addEventListener('submit', (e) => this.onEmailSubmit(e));
      this.nextBtn.addEventListener('click', () => this.onNext());
      this.backBtn.addEventListener('click', () => this.onBack());
      if (this.skipBtn) this.skipBtn.addEventListener('click', () => this.onSkipEmail());
      this.restart.addEventListener('click', () => this.reset());

      this.render();
    }

    onChange(e) {
      const key = QUESTION_KEYS[this.step];
      const val = this.form.querySelector(`input[name="${key}"]:checked`);
      if (val) {
        this.answers[key] = val.value;
        this.nextBtn.disabled = false;
      }
    }

    onNext() {
      const key = QUESTION_KEYS[this.step];
      if (!this.answers[key]) return;

      if (typeof gtag === 'function') {
        gtag('event', 'quiz_step', {
          event_category: 'Mattress Quiz',
          quiz_step: this.step + 1,
          quiz_question: key,
          quiz_answer: this.answers[key]
        });
      }

      if (this.step < QUESTION_KEYS.length - 1) {
        this.step++;
        this.render();
      } else {
        // Step 4 (index 3, last QUESTION_KEYS step) Next → email-capture step (index 4)
        // unless user already submitted this session, in which case skip straight to result.
        if (this.hasSubmittedThisSession()) {
          this.showResult();
        } else {
          this.step = QUESTION_KEYS.length; // index 4
          this.render();
        }
      }
    }

    hasSubmittedThisSession() {
      try {
        return sessionStorage.getItem('gn-quiz-submitted') === '1';
      } catch (e) {
        return false;
      }
    }

    markSubmittedThisSession() {
      try { sessionStorage.setItem('gn-quiz-submitted', '1'); } catch (e) { /* private mode */ }
    }

    onSkipEmail() {
      if (typeof gtag === 'function') {
        gtag('event', 'quiz_email_skip', { event_category: 'Mattress Quiz' });
      }
      this.showResult();
    }

    onEmailSubmit(e) {
      e.preventDefault();
      const email = (this.emailInput && this.emailInput.value || '').trim();
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!valid) {
        if (this.emailError) this.emailError.hidden = false;
        this.emailInput.focus();
        return;
      }
      if (this.emailError) this.emailError.hidden = true;

      // Abort any in-flight redemption fetch from a previous submit (race-safe).
      if (this._fetchController) {
        try { this._fetchController.abort(); } catch (_) { /* noop */ }
      }

      // Disable submit + show loading state
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.dataset.originalLabel = this.submitBtn.textContent;
        this.submitBtn.textContent = 'Locking in your code…';
      }

      // Honeypot field (intentionally hidden in markup; non-bots leave it empty).
      const honeypotEl = this.querySelector('[data-gn-honeypot]');
      const honeypot = honeypotEl ? honeypotEl.value : '';

      const controller = new AbortController();
      this._fetchController = controller;
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      fetch('https://automations.pebblesleep.com/api/quiz/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, answers: this.answers, honeypot }),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => {
          clearTimeout(timeoutId);
          this.markSubmittedThisSession();
          this.redeemResult = data;
          if (typeof gtag === 'function') {
            gtag('event', 'quiz_email_submit', {
              event_category: 'Mattress Quiz',
              quiz_email_domain: email.split('@')[1] || '',
              quiz_eligible: data?.eligible ? 'yes' : 'no',
            });
          }
          this.showResult();
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          // On timeout / network error, still show the result with error copy.
          this.redeemResult = {
            ok: false,
            eligible: false,
            code: null,
            copy: {
              headline: 'Our discount engine hit snooze.',
              body: "Try refreshing? If it keeps napping, email hello@pebblesleep.com and we'll set you up manually.",
              subnote: '',
            },
          };
          this.showResult();
        })
        .finally(() => {
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            if (this.submitBtn.dataset.originalLabel) {
              this.submitBtn.textContent = this.submitBtn.dataset.originalLabel;
            }
          }
        });
    }

    onBack() {
      if (this.step > 0) {
        this.step--;
        this.render();
      }
    }

    render() {
      this.steps.forEach((s) => { s.hidden = parseInt(s.dataset.gnStep, 10) !== this.step; });
      const isEmailStep = this.step === QUESTION_KEYS.length;

      if (!isEmailStep) {
        this.stepLabel.hidden = false;
        this.stepLabel.textContent = `Question ${this.step + 1} of ${QUESTION_KEYS.length}`;
        this.progress.forEach((dot, i) => {
          dot.classList.remove('is-past', 'is-current');
          if (i < this.step) dot.classList.add('is-past');
          if (i === this.step) dot.classList.add('is-current');
        });
        if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(this.step + 1));
        if (this.announcer) {
          const activeStep = this.querySelector(`[data-gn-step="${this.step}"]`);
          const legend = activeStep && activeStep.querySelector('.gn-quiz__question');
          const questionText = legend ? legend.textContent.trim() : '';
          this.announcer.textContent = `Step ${this.step + 1} of ${QUESTION_KEYS.length}: ${questionText}`;
        }
      } else {
        // Email step — hide the question-progress chrome, mark progressbar at max.
        this.stepLabel.hidden = true;
        this.progress.forEach((dot) => {
          dot.classList.remove('is-current');
          dot.classList.add('is-past');
        });
        if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', String(QUESTION_KEYS.length));
        if (this.announcer) {
          this.announcer.textContent = 'Almost done — enter your email to see your match and get 15% off your first order.';
        }
      }

      this.backBtn.hidden = this.step === 0;
      this.nextBtn.hidden = isEmailStep;
      if (this.submitBtn) this.submitBtn.hidden = !isEmailStep;
      if (this.skipBtn) this.skipBtn.hidden = !isEmailStep;

      if (!isEmailStep) {
        this.nextBtn.textContent = this.step < QUESTION_KEYS.length - 1 ? 'Next' : 'Get my match';
        const key = QUESTION_KEYS[this.step];
        this.nextBtn.disabled = !this.answers[key];
      }
    }

    showResult() {
      const ranked = score(this.answers);
      const best = ranked[0];
      const also = ranked.slice(1, 3);

      const bestEl = this.querySelector('[data-gn-best]');
      const alsoEl = this.querySelector('[data-gn-also]');
      const whyEl  = this.querySelector('[data-gn-why]');

      const bestData = this.products[best.handle];
      if (bestData) bestEl.innerHTML = cardHTML(bestData, 'Best match');
      alsoEl.innerHTML = also
        .map(r => this.products[r.handle] ? cardHTML(this.products[r.handle], '') : '')
        .join('');
      const chipsEl = this.querySelector('[data-gn-chips]');
      if (chipsEl) {
        chipsEl.innerHTML = QUESTION_KEYS
          .map(k => (this.answers[k] && ANSWER_LABEL[k] && ANSWER_LABEL[k][this.answers[k]])
            ? `<span class="gn-quiz__chip">${ANSWER_LABEL[k][this.answers[k]]}</span>`
            : '')
          .join('');
      }
      whyEl.innerHTML = QUESTION_KEYS
        .map(k => (this.answers[k] && WHY[k] && WHY[k][this.answers[k]])
          ? `<li>${WHY[k][this.answers[k]]}</li>`
          : '')
        .join('');

      // Render offer block from redeem-endpoint response (if user submitted email).
      const offerEl = this.querySelector('[data-gn-offer]');
      if (offerEl) {
        offerEl.innerHTML = this.renderOffer();
        this.startCountdown();
      }

      this.form.hidden = true;
      this.querySelector('.gn-quiz__progress').hidden = true;
      this.stepLabel.hidden = true;
      this.result.hidden = false;

      if (typeof gtag === 'function') {
        gtag('event', 'quiz_complete', {
          event_category: 'Mattress Quiz',
          quiz_position: this.answers.position,
          quiz_firmness: this.answers.firmness,
          quiz_hot: this.answers.hot,
          quiz_size: this.answers.size,
          quiz_result_1: ranked[0].handle,
          quiz_result_2: ranked[1].handle,
          quiz_result_3: ranked[2].handle
        });
      }
    }

    disconnectedCallback() {
      if (this._countdownInterval) clearInterval(this._countdownInterval);
    }

    renderOffer() {
      const data = this.redeemResult;
      if (!data) return '';  // user skipped email
      const copy = data.copy || {};

      if (data.ok && data.eligible && data.code) {
        const expiresAttr = data.expiresAt
          ? ` data-gn-offer-expires="${this.escapeHtml(String(data.expiresAt))}"`
          : '';
        return `
          <div class="gn-quiz__offer gn-quiz__offer--eligible"${expiresAttr}>
            <h3 class="gn-quiz__offer-headline">${this.escapeHtml(copy.headline || '')}</h3>
            <button type="button" class="gn-quiz__code" data-gn-code aria-label="Copy code ${this.escapeHtml(data.code)}">
              <span class="gn-quiz__code-value">${this.escapeHtml(data.code)}</span>
              <span class="gn-quiz__code-copy">Tap to copy</span>
            </button>
            <p class="gn-quiz__offer-body">${this.escapeHtml(copy.body || '')}</p>
            <p class="gn-quiz__offer-subnote" data-gn-countdown>${this.escapeHtml(copy.subnote || '')}</p>
          </div>`;
      }

      // Not-eligible, error, rate-limit, etc. — render headline + body, no code block
      return `
        <div class="gn-quiz__offer gn-quiz__offer--info">
          <h3 class="gn-quiz__offer-headline">${this.escapeHtml(copy.headline || '')}</h3>
          <p class="gn-quiz__offer-body">${this.escapeHtml(copy.body || '')}</p>
          ${copy.subnote ? `<p class="gn-quiz__offer-subnote">${this.escapeHtml(copy.subnote || '')}</p>` : ''}
        </div>`;
    }

    startCountdown() {
      // Clear any previously running countdown before starting a new one.
      if (this._countdownInterval) {
        clearInterval(this._countdownInterval);
        this._countdownInterval = null;
      }
      const offerEl = this.querySelector('[data-gn-offer] .gn-quiz__offer--eligible');
      if (!offerEl) return;
      const expiresIso = offerEl.dataset.gnOfferExpires;
      if (!expiresIso) return;
      const expiresAt = new Date(expiresIso).getTime();
      const countdownEl = offerEl.querySelector('[data-gn-countdown]');
      if (!countdownEl) return;

      const tick = () => {
        const remaining = Math.max(0, expiresAt - Date.now());
        const totalSec = Math.floor(remaining / 1000);
        const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
        const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
        const s = String(totalSec % 60).padStart(2, '0');
        countdownEl.textContent = `Expires in ${h}:${m}:${s}`;
        if (remaining <= 0) {
          clearInterval(this._countdownInterval);
          countdownEl.textContent = 'This code has expired.';
          const codeBtn = offerEl.querySelector('[data-gn-code]');
          if (codeBtn) codeBtn.disabled = true;
        }
      };
      tick();
      this._countdownInterval = setInterval(tick, 1000);

      // Click-to-copy
      const codeBtn = offerEl.querySelector('[data-gn-code]');
      if (codeBtn) {
        codeBtn.addEventListener('click', () => {
          const code = codeBtn.querySelector('.gn-quiz__code-value')?.textContent;
          if (!code || !navigator.clipboard) return;
          navigator.clipboard.writeText(code).then(() => {
            const copyLabel = codeBtn.querySelector('.gn-quiz__code-copy');
            if (copyLabel) {
              const original = copyLabel.textContent;
              copyLabel.textContent = 'Copied ✓';
              setTimeout(() => { copyLabel.textContent = original; }, 2000);
            }
          }).catch(() => { /* user-initiated failure (tab switched, permissions revoked) — silent */ });
        });
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = String(str || '');
      return div.innerHTML;
    }

    reset() {
      if (this._countdownInterval) clearInterval(this._countdownInterval);
      this.redeemResult = null;
      const offerEl = this.querySelector('[data-gn-offer]');
      if (offerEl) offerEl.innerHTML = '';
      this.step = 0;
      this.answers = { position: null, firmness: null, hot: null, size: null };
      this.form.querySelectorAll('input[type="radio"]').forEach(r => { r.checked = false; });
      if (this.emailInput) this.emailInput.value = '';
      if (this.emailError) this.emailError.hidden = true;
      this.form.hidden = false;
      this.querySelector('.gn-quiz__progress').hidden = false;
      this.stepLabel.hidden = false;
      this.result.hidden = true;
      this.render();
    }
  }

  if (typeof customElements !== 'undefined' && customElements.get && !customElements.get('goodnight-mattress-quiz')) {
    customElements.define('goodnight-mattress-quiz', GoodnightMattressQuiz);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { score };
  }
})();
